import { CoursePagination, CoursePaginationParameters } from "@carnegie-learning/data-access-angular-passport";
import { User } from "@carnegie-learning/data-access-fido";
import { Action, createReducer, on } from "@ngrx/store";
import { Course, Student, WelcomeContentInterface } from "cli-types";
import * as fromCoursesActions from "../actions/teacher-classes.action";

export const defaultCoursePaginationParams: CoursePaginationParameters = {
    limit: 1000,
    page: 0,
    filter: { archived: false }, // true - only archived records, false - only active records, don't include param means everything
    search: "",
    sorting: { courseTitle: "asc" },
    // withCourseMetrics filter flag is slow, use with caution.
    withCourseMetrics: false,
    withSubmittedMetrics: true,
};

// creating an interface to say what type the initialState will be
// using entities (data objects) instead of arrays is better for perforance
export interface CoursesListState {
    entities: Map<number, Course>;
    loaded: boolean;
    loading: boolean;
    rawResponse: CoursePagination | null;
    paginationParams: CoursePaginationParameters;
    selectedCourse: Course | null;
    dropdownEntities: Map<number, Course> | null;
    dropdownLoaded: boolean;
    dropdownLoading: boolean;
    filterBoxOpened: boolean;
    coursesBackground: WelcomeContentInterface | null;
    coursesBackgroundLoaded: boolean;
    coursesBackgroundLoading: boolean;
    // Commenting out for PAS-11616
    // backgroundImage: WelcomeContentInterface | null;
    // backgroundImageLoaded: boolean;
    // backgroundImageLoading: boolean;
    fidoStudentsData: User[];
}

// set initial state as type CoursesListState
export const initialState: CoursesListState = {
    entities: new Map(),
    loaded: false,
    loading: false,
    rawResponse: null,
    paginationParams: defaultCoursePaginationParams,
    selectedCourse: null,
    dropdownEntities: null, // THIS needs to be initialized to null so we can tell the difference between unloaded results and NO results.
    dropdownLoaded: false,
    dropdownLoading: false,
    filterBoxOpened: false,
    coursesBackground: null,
    coursesBackgroundLoaded: false,
    coursesBackgroundLoading: false,
    // Commenting out for PAS-11616
    // backgroundImage: null,
    // backgroundImageLoaded: false,
    // backgroundImageLoading: false,
    fidoStudentsData: [],
};
const coursesListReducer = createReducer(
    initialState,
    on(
        fromCoursesActions.loadCourses,
        fromCoursesActions.loadCourse,
        fromCoursesActions.updateRosterCoursesBulk,
        (state) => {
            return {
                ...state,
                loading: true,
                loaded: false,
            };
        }
    ),
    on(fromCoursesActions.setCoursesPaginationParams, (state, { payload }) => {
        // Merge parameter values, don't overwrite like LOAD_RESOURCES does.
        const inputParams = payload;
        const paginationParams: CoursePaginationParameters = {
            ...state.paginationParams,
        };
        const filter = {};
        Object.keys(inputParams).forEach(function (key) {
            (<any>paginationParams)[key] = inputParams[key]; // TODO: Find a better answer than this
        });

        return {
            ...state,
            paginationParams: paginationParams,
        };
    }),
    on(fromCoursesActions.loadCoursesSuccess, (state, { payload }) => {
        const courses: Course[] = payload.data;
        const entities = new Map<number, Course>();

        courses.forEach((course) => {
            entities.set(course.courseID, course);
        });

        return {
            ...state,
            loading: false,
            loaded: true,
            entities: entities,
            rawResponse: payload,
        };
    }),
    on(
        fromCoursesActions.loadCoursesFail,
        fromCoursesActions.loadCourseFail,
        (state) => {
            return {
                ...state,
                loading: false,
                loaded: false,
            };
        }
    ),
    on(
        fromCoursesActions.loadCoursesDropdown,
        fromCoursesActions.setCoursesDropdownLoading,
         (state) => {
        return {
            ...state,
            dropdownLoading: true,
            dropdownLoaded: false,
        };
    }),
        on(
        fromCoursesActions.setCoursesDropdownLoaded,
         (state) => {
        return {
            ...state,
            dropdownLoading: false,
            dropdownLoaded: true,
        };
    }),
    // on(fromCoursesActions.loadCoursesDropdownSuccess, (state, { payload }) => {
    //     const teacher-classes = payload.data;
    //     // create the entity const
    //     const dropdownEntities = new Map<number, Course>();
    //     (<Course[]>teacher-classes).forEach((teacher-class) => {
    //         // const sortedStudents = teacher-class.students
    //         //     .slice()
    //         //     .sort((a, b) => a.lastName.localeCompare(b.lastName));
    //
    //         dropdownEntities.set(teacher-class.courseID, {
    //             ...teacher-class,
    //             students: teacher-class.students,
    //         });
    //     });
    //
    //     // then re-bind the entity const with our returned state
    //     return {
    //         ...state,
    //         dropdownLoading: false,
    //         dropdownLoaded: true,
    //         dropdownEntities: dropdownEntities,
    //     };
    // }),
    on(
        fromCoursesActions.loadCoursesFidoStudentsDropdownSuccess,
        (state, { courses, fidoData }) => {
            // Build student Map to quickly access Fido data
            const fidoStudents = new Map<string, User>();
            fidoData.forEach((fidoUser) => {
                fidoStudents.set(fidoUser.id, fidoUser);
            });
            const entities = new Map<number, Course>(state.entities.entries());

            // create the entity const
            const dropdownEntities = new Map<number, Course>();
            (<Course[]>courses).forEach((course) => {
                // const sortedStudents = teacher-class.students
                //     .slice()
                //     .sort((a, b) => a.lastName.localeCompare(b.lastName));
                const courseStudents: Student[] = []; // need to append fido first/last name
                course.students.forEach((student) => {
                    const studentFidoData = fidoStudents.get(student.userGUID);
                    if (studentFidoData && student.firstName === null) {
                        courseStudents.push(<Student>{
                            ...student,
                            firstName: studentFidoData.givenName,
                            lastName: studentFidoData.surName,
                        });
                    } else {
                        courseStudents.push(student);
                    }
                });
                const stateCourse = entities.get(course.courseID);
                entities.set(course.courseID, {
                    ...stateCourse,
                    ...course,
                    students: courseStudents,
                });
                dropdownEntities.set(course.courseID, {
                    ...stateCourse,
                    ...course,
                    students: courseStudents,
                });
            });

            // then re-bind the entity const with our returned state
            return {
                ...state,
                dropdownLoading: false,
                dropdownLoaded: true,
                dropdownEntities: dropdownEntities,
                fidoStudentsData: fidoData,
                entities,
            };
        }
    ),
    on(
        fromCoursesActions.updateRosterCoursesBulkSuccess,
        (state, { payload }) => {
            const courses = payload;

            const updatedEntities = new Map<number, Course>(
                state.entities.entries()
            );

            let updatedDropdownEntities: Map<number, Course> | null = null;
            if (state.dropdownEntities) {
                updatedDropdownEntities = new Map<number, Course>(
                    state.dropdownEntities.entries()
                );
            }
            // update the activated books and languageID foreach teacher-class
            courses.forEach((updatedCourse) => {
                const course: Course | undefined = state.entities.get(
                    updatedCourse.courseID
                );
                if (course) {
                    // Only update existing teacher-classes
                    updatedEntities.set(updatedCourse.courseID, {
                        ...course,
                        language: updatedCourse.language,
                        languageID: updatedCourse.languageID,
                        languageName: updatedCourse.language.languageName,
                        level: updatedCourse.level,
                    });
                    if (state.dropdownEntities && updatedDropdownEntities) {
                        updatedDropdownEntities.set(
                            updatedCourse.courseID,
                            updatedCourse
                        );
                    }
                }
            });

            return {
                ...state,
                loading: false,
                loaded: true,
                entities: updatedEntities,
                dropdownEntities: updatedDropdownEntities,
            };
        }
    ),
    on(fromCoursesActions.updateRosterCoursesBulkFail, (state) => {
        return {
            ...state,
            loading: false,
            loaded: true,
        };
    }),
    on(fromCoursesActions.loadCourseSuccess, (state, { payload }) => {
        const course = payload;

        const updatedEntities = new Map<number, Course>(
            state.entities.entries()
        );
        updatedEntities.set(course.courseID, course);

        return {
            ...state,
            loading: false,
            loaded: true,
            entities: updatedEntities,
        };
    }),
    on(
        fromCoursesActions.createCourseSuccess,
        fromCoursesActions.updateCourseSuccess,
        (state, { payload }) => {
            let course = payload;
            // create an updated representation of our entities (data with new teacher-class)
            const updatedEntities = new Map<number, Course>(
                state.entities.entries()
            );
            const OriCourse = updatedEntities.get(course.courseID);
            if (OriCourse) {
                course = {
                    ...OriCourse,
                    ...course,
                };
            }
            updatedEntities.set(course.courseID, course);

            let updatedDropdownEntities: Map<number, Course> | null = null;
            if (state.dropdownEntities) {
                updatedDropdownEntities = new Map<number, Course>(
                    state.dropdownEntities.entries()
                );
                updatedDropdownEntities.set(course.courseID, course);
            }

            return {
                ...state,
                entities: updatedEntities,
                dropdownEntities: updatedDropdownEntities,
            };
        }
    ),
    on(
        fromCoursesActions.archiveCourseSuccess,
        fromCoursesActions.removeCourseSuccess,
        (state, { payload }) => {
            // gain access to the teacher-class
            const course = payload;
            const entities = new Map<number, Course>(state.entities.entries());
            entities.delete(course.courseID);

            let updatedDropdownEntities: Map<number, Course> | null = null;
            if (state.dropdownEntities) {
                // Distributor has null for these entities.
                updatedDropdownEntities = new Map<number, Course>(
                    state.dropdownEntities.entries()
                );
                updatedDropdownEntities.delete(course.courseID);
            }

            // If the teacher-class we just archived or deleted is the selected teacher-class, set selectedCourse to null.
            let selectedCourse = state.selectedCourse;
            if (selectedCourse && selectedCourse.courseID === course.courseID) {
                selectedCourse = null;
            }

            return {
                ...state,
                entities: entities,
                dropdownEntities: updatedDropdownEntities,
                selectedCourse: selectedCourse,
            };
        }
    ),
    on(fromCoursesActions.reactivateCourseSuccess, (state, { payload }) => {
        // gain access to the teacher-class
        const course = payload;
        const entities = new Map<number, Course>(state.entities.entries());
        entities.delete(course.courseID);

        let updatedDropdownEntities: Map<number, Course> | null = null;
        if (state.dropdownEntities) {
            // Since we have reactivated the teacher-class, we want to add the entity back into the list.
            updatedDropdownEntities = new Map<number, Course>(
                state.dropdownEntities.entries()
            );
            updatedDropdownEntities.set(course.courseID, course);
        }

        return {
            ...state,
            entities: entities,
            dropdownEntities: updatedDropdownEntities,
        };
    }),
    on(fromCoursesActions.setSelectedCourse, (state, { payload }) => {
        const course = payload;

        return {
            ...state,
            selectedCourse: course,
        };
    }),
    on(fromCoursesActions.loadSelectedCourseSuccess, (state, { course }) => {
        return {
            ...state,
            selectedCourse: course,
        };
    }),
    on(
        fromCoursesActions.loadCourseMetricsSuccess,
        (state, { courseID, courseMetrics }) => {
            let updatedCourse: Course | undefined =
                state.entities.get(courseID);
            const updatedCourseMetrics = courseMetrics;
            const entities = new Map<number, Course>(state.entities.entries());

            if (updatedCourse) {
                // Update the teacher-class with the updatedCourseMetrics
                updatedCourse = {
                    ...updatedCourse,
                    courseMetrics: updatedCourseMetrics,
                };

                // Update the entities with the updatedCourse
                entities.set(courseID, updatedCourse);
            }

            return {
                ...state,
                entities: entities,
            };
        }
    ),
    on(fromCoursesActions.clearCourses, (state) => {
        return {
            ...state,
            entities: new Map(),
            loaded: false,
            loading: false,
            rawResponse: null,
            paginationParams: { ...state.paginationParams }, // We don't need to modify since we don't paginate
            selectedCourse: null,
        };
    }),
    on(
        fromCoursesActions.addCourseStudent,
        fromCoursesActions.reactivateCourseStudent,
        (state, { payload, courseID }) => {
            const course: Course | undefined = state.entities.get(courseID);
            let updatedStudents: Student[] | undefined = course?.students;
            const newStudent = payload;
            const entities = new Map<number, Course>(state.entities.entries());

            if (course && updatedStudents) {
                // Add new student to current array of students
                updatedStudents = [...updatedStudents, newStudent];

                // Update teacher-class with the updatedStudents
                course.students = updatedStudents;

                // Update the entities with the updatedCourse
                entities.set(courseID, course);
            }

            return {
                ...state,
                entities: entities,
            };
        }
    ),
    on(
        fromCoursesActions.updateCourseStudent,
        (state, { payload, courseID }) => {
            const course: Course | undefined = state.entities.get(courseID);
            const updatedStudent = payload;
            const entities = new Map<number, Course>(state.entities.entries());

            if (course?.students) {
                // get student's index so we can properly update(set) the student
                const foundStudent = course.students.findIndex(
                    (s) => s.userID === updatedStudent.userID
                );

                // create map of teacher-class's students and update the passed in student
                const students = new Map<number, Student>(
                    course.students.entries()
                );
                students.set(foundStudent, updatedStudent);

                // convert students map back to array of values
                const updatedStudentsArray = Array.from(students.values());

                // set teacher-class's students as updated student array
                course.students = updatedStudentsArray;

                // Update the entities with the updatedCourse
                entities.set(courseID, course);
            }

            return {
                ...state,
                entities: entities,
            };
        }
    ),
    on(
        fromCoursesActions.archiveCourseStudent,
        (state, { payload, courseID }) => {
            const course: Course | undefined = state.entities.get(courseID);
            const archivedStudent = payload;
            const entities = new Map<number, Course>(state.entities.entries());

            if (course?.students) {
                // get student's index so we can archive the correct student
                const foundStudent = course.students.findIndex(
                    (s) => s.userID === archivedStudent.userID
                );

                // create map of teacher-class's students and remove the passed in student
                const students = new Map<number, Student>(
                    course.students.entries()
                );
                students.delete(foundStudent);

                // convert students map back to array of values
                const updatedStudentsArray = Array.from(students.values());

                // set teacher-class's students as updated student array
                course.students = updatedStudentsArray;

                // Update the entities with the updatedCourse
                entities.set(courseID, course);
            }

            return {
                ...state,
                entities: entities,
            };
        }
    ),
    on(fromCoursesActions.setCourseFilterBoxOpened, (state, { payload }) => {
        return {
            ...state,
            filterBoxOpened: payload,
        };
    }),
    on(fromCoursesActions.loadCoursesBackground, (state) => {
        return {
            ...state,
            coursesBackgroundLoading: true,
        };
    }),
    on(
        fromCoursesActions.loadCoursesBackgroundSuccess,
        (state, { payload }) => {
            return {
                ...state,
                coursesBackgroundLoading: false,
                coursesBackgroundLoaded: true,
                coursesBackground: payload,
            };
        }
    ),
    on(fromCoursesActions.loadCoursesBackgroundFail, (state) => {
        return {
            ...state,
            coursesBackgroundLoading: false,
            coursesBackgroundLoaded: false,
        };
    }),
    // Commenting out for PAS-11616
    // on(fromCoursesActions.loadBackgroundImage, (state) => {
    //     return {
    //         ...state,
    //         backgroundImageLoading: true,
    //     };
    // }),
    // on(fromCoursesActions.loadBackgroundImageSuccess, (state, { payload }) => {
    //     return {
    //         ...state,
    //         backgroundImageLoading: false,
    //         backgroundImageLoaded: true,
    //         backgroundImage: payload,
    //     };
    // }),
    // on(fromCoursesActions.loadBackgroundImageFail, (state) => {
    //     return {
    //         ...state,
    //         backgroundImageLoading: false,
    //         backgroundImageLoaded: false,
    //     };
    // }),
    on(fromCoursesActions.increaseStudentCount, (state, { courseID }) => {
        const course: Course | undefined = state.entities.get(courseID);

        let updatedCourse: Course | null = course ? { ...course } : null;
        if (course && course.studentCount) {
            updatedCourse = {
                ...course,
                studentCount: course.studentCount + 1,
            };
        }

        // Update the entities with the updatedCourse
        const entities = new Map<number, Course>(state.entities.entries());
        if (updatedCourse) {
            entities.set(courseID, updatedCourse);
        }

        return {
            ...state,
            entities: entities,
            selectedCourse: updatedCourse,
        };
    }),
    on(fromCoursesActions.decreaseStudentCount, (state, { courseID }) => {
        const course: Course | undefined = state.entities.get(courseID);

        let updatedCourse: Course | null = course ? { ...course } : null;
        if (course && course.studentCount && course.studentCount > 0) {
            updatedCourse = {
                ...course,
                studentCount: course.studentCount - 1,
            };
        }

        // Update the entities with the updatedCourse
        const entities = new Map<number, Course>(state.entities.entries());
        if (updatedCourse) {
            entities.set(courseID, updatedCourse);
        }

        return {
            ...state,
            entities: entities,
            selectedCourse: updatedCourse ? updatedCourse : null,
        };
    })
);
export function reducer(state: CoursesListState | undefined, action: Action) {
    return coursesListReducer(state, action);
}

// this is where we want to export some levels of state
export const getCoursesEntities = (state: CoursesListState) => state.entities;
export const getCoursesLoading = (state: CoursesListState) => state.loading;

// export loaded and filtered against selectedCourse
export const getCoursesLoaded = (state: CoursesListState) => state.loaded;
export const getCoursesRawResponse = (state: CoursesListState) =>
    state.rawResponse;
export const getCoursesPaginationParams = (state: CoursesListState) =>
    state.paginationParams;
export const getSelectedCourseObject = (state: CoursesListState) =>
    state.selectedCourse;

export const getDropdownCoursesEntities = (state: CoursesListState) =>
    state.dropdownEntities;
export const getDropdownCoursesLoading = (state: CoursesListState) =>
    state.dropdownLoading;
export const getDropdownCoursesLoaded = (state: CoursesListState) =>
    state.dropdownLoaded;
export const getCourseFilterBoxOpened = (state: CoursesListState) =>
    state.filterBoxOpened;

// Background Image State
export const getCoursesBackground = (state: CoursesListState) =>
    state.coursesBackground;
// Commenting out for PAS-11616
// export const getBackgroundImage = (state: CoursesListState) =>
//     state.backgroundImage;
// export const getBackgroundImageLoading = (state: CoursesListState) =>
//     state.backgroundImageLoading;
// export const getBackgroundImageLoaded = (state: CoursesListState) =>
//     state.backgroundImageLoaded;
export const getFidoStudentsData = (state: CoursesListState) =>
    state.fidoStudentsData;
